/**
 * 路由元数据配置（用于面包屑等）
 * 独立文件避免循环依赖
 */
export interface RouteMeta {
  name: string; // 路由名称/面包屑标题
  parent?: string; // 父级路由路径
  back?: boolean; // 点击面包屑时是否使用返回而非跳转
}

/**
 * 路由元数据映射
 * key: 路由路径
 * value: 路由元数据
 */
export const routeMetaMap: Record<string, RouteMeta> = {
  // 首页
  '/index': { name: '首页' },

  // 患者相关
  '/patient/imdtAgent': { name: 'IMDT病历智能体', parent: '/index' },
  '/patient/electronicEditor': { name: '电子病历编辑器', parent: '/index' },
  '/index/patient-detail': { name: '完善患者病例', parent: '/index' },
  '/personal/userinfo': { name: '个人中心', parent: '/index' },
  '/personal-center/index': { name: '个人中心', parent: '/index' },
  '/patient/patientInviteCode': { name: '授权邀请码', parent: '/index' },
  '/patient/list': { name: '患者病历', parent: '/index' },
  '/patient/detail': { name: '患者病历详情', parent: '/patient/list' },
  '/patient/patientDetail': { name: '患者详情', parent: '/patient/list' },
  '/patient/patientDetailForm': { name: '新增记录', parent: '/patient/patientDetail', back: true },
  '/patient/detail/patientCaseTag': { name: '编辑标签', parent: '/patient/detail', back: true },
  '/patient/detail/patientSummary': { name: '编辑病史摘要', parent: '/patient/detail', back: true },
  '/patient/detail/patientInfoDetail': {
    name: '患者信息详情',
    parent: '/patient/detail',
    back: true,
  },
  '/patient/detail/patientInfoEdit': {
    name: '患者信息编辑',
    parent: '/patient/detail',
    back: true,
  },
  '/patient/detail/patientOcr': { name: '患者OCR', parent: '/patient/detail', back: true },

  // 讲稿中心
  '/lecture-center/index': { name: '讲稿中心', parent: '/index' },

  // MDT团队
  '/mdt-team/index': { name: 'MDT团队', parent: '/index' },
  '/mdt-team/detail': { name: '团队详情', parent: '/mdt-team/index' },

  // 会诊中心
  '/consultation-center/index': { name: 'MDT会诊', parent: '/index' },
  '/consultation-center/detail': { name: '会诊详情', parent: '/consultation-center/index' },
  '/consultation-center/meetingDetail': { name: '会诊详情', parent: '/consultation-center/index' },
  '/consultation-center/create': { name: '创建会诊', parent: '/consultation-center/index' },
  '/consultation-center/editMeetingMinutes': {
    name: '编辑会诊纪要',
    parent: '/consultation-center/index',
  },
  '/consultation-center/meetingMinutesForm': {
    name: '编辑会诊纪要',
    parent: '/consultation-center/index',
  },
  '/consultation-center/issueVoteDetail': {
    name: '投票详情',
    parent: '/consultation-center/index',
  },
  '/consultation-center/pictureTextMeeting': {
    name: '图文会诊',
    parent: '/consultation-center/index',
  },

  // 病例库
  '/case-history/index': { name: '病例列表', parent: '/index' },
  '/case-history/case': { name: '项目病例库', parent: '/index' },
  '/case-history/share': { name: '共享病例库', parent: '/index' },
  '/case-history/consultat-detail': { name: '会诊详情', parent: '/case-history/share' },

  // 会诊讲稿
  '/speech-lecture/index': { name: '会诊讲稿', parent: '/index' },
  '/speech-lecture/making': { name: '制作讲稿', parent: '/speech-lecture/index' },
  '/speech-lecture/preview': { name: '讲稿预览', parent: '/speech-lecture/index' },
  '/speech-lecture/lecture': { name: '讲稿预览', parent: '/speech-lecture/index' },

  // 合同
  '/contract/verification': { name: '审核列表', parent: '/index' },
  '/contract/template': { name: '合同模板', parent: '/index' },
  '/contract/preview': { name: '合同预览', parent: '/contract/verification' },

  // 系统管理
  '/sys/accountNumber': { name: '账号管理', parent: '/index' },
  '/sys/roleAdmin': { name: '角色管理', parent: '/index' },
  '/sys/menuAdmin': { name: '菜单管理', parent: '/index' },
  '/sys/groupAdmin': { name: '权限组管理', parent: '/index' },
  '/sys/diseaseTypeAdmin': { name: '病种管理', parent: '/index' },

  // 主数据管理
  '/masterDataManagement': { name: '主数据管理', parent: '/index' },
  '/masterDataManagement/labelAdmin': { name: '系统标签管理', parent: '/index' },
  '/masterDataManagement/regionAdmin': { name: '区域管理', parent: '/index' },
  '/masterDataManagement/hospitalAdmin': { name: '医院管理', parent: '/index' },
  '/masterDataManagement/departmentAdmin': { name: '科室管理', parent: '/index' },
  '/masterDataManagement/jobTitleAdmin': { name: '职称管理', parent: '/index' },

  // BI配置
  '/bIModule/bIConfiguration': { name: 'BI配置', parent: '/index' },

  // 申请管理
  '/applyAdmin/consultationApply': { name: '会诊申请', parent: '/index' },
  '/applyAdmin/cooperationApply': { name: '合作申请', parent: '/index' },

  // 机构管理
  '/organizational': { name: '机构信息', parent: '/index' },
  '/organizational/edit': { name: '编辑机构信息', parent: '/organizational' },
  '/project': { name: '项目信息', parent: '/index' },
  '/organizational/project': { name: '项目信息', parent: '/index' },
  '/project/edit': { name: '编辑项目信息', parent: '/project' },

  // 公众号管理
  '/officialAccount/officialList': { name: '公众号网站管理', parent: '/index' },
  '/officialAccount/allianceDetails': { name: '联盟详情', parent: '/officialAccount/officialList' },
  '/officialAccount/editorialAlliance': {
    name: '编辑联盟',
    parent: '/officialAccount/officialList',
  },
  '/officialAccount/createTeam': { name: '创建联盟', parent: '/officialAccount/officialList' },
  '/officialAccount/createActivity': { name: '创建活动', parent: '/officialAccount/officialList' },

  // 医生管理
  '/doctor/list': { name: '医生管理', parent: '/index' },
  '/doctor/edit': { name: '医生编辑', parent: '/doctor/list' },
  '/doctor/create': { name: '创建医生', parent: '/doctor/list' },
  '/doctor/detail': { name: 'MDT详情', parent: '/doctor/list' },
  '/doctor/doctorPatient': { name: '病例列表', parent: '/doctor/list' },
  '/doctor/info/list': { name: '医生信息库', parent: '/index' },
  '/doctor/info/detail': { name: '医生详情', parent: '/doctor/info/list' },
  '/doctor/user': { name: '医生用户管理', parent: '/index' },
  '/doctor/user/detail': { name: '医生用户详情', parent: '/doctor/user' },
  '/doctor/user/regiStatistics': { name: '注册数据统计', parent: '/index' },

  // 医助管理
  '/doctor/assistant/list': { name: '医助管理', parent: '/index' },
  '/doctor/assistant/detail': { name: '医助详情', parent: '/doctor/assistant/list' },

  // MDT评价
  '/scoreModule/scorelist': { name: 'MDT评价', parent: '/index' },
  '/scoreModule/scoreDetails': { name: '评分详情', parent: '/scoreModule/scorelist' },

  // 订单管理
  '/orderAdmin/orderList': { name: '订单管理', parent: '/index' },
  '/orderAdmin/orderDetails': { name: '订单详情', parent: '/orderAdmin/orderList' },

  // 交易流水
  '/transactionFlow/transactionList': { name: '交易流水', parent: '/index' },

  // 招募管理
  '/consultationRecruitment/recruitmentManage': { name: '招募管理', parent: '/index' },
  '/consultationRecruitment/recruitmentManageDetail': {
    name: '招募详情',
    parent: '/consultationRecruitment/recruitmentManage',
  },
  '/consultationRecruitment/signUp': { name: '报名管理', parent: '/index' },
  '/consultationRecruitment/recruitment': { name: '招募列表', parent: '/index' },
  '/consultationRecruitment/recruitmentDetails': {
    name: '招募详情',
    parent: '/consultationRecruitment/recruitment',
  },

  // 表单库
  '/formily/list': { name: '表单库', parent: '/index' },
  '/formily/editor': { name: '表单编辑', parent: '/formily/list' },

  // 随访管理
  '/followUp/list': { name: '随访管理', parent: '/index' },
  '/followUp/detail': { name: '随访详情', parent: '/followUp/list' },

  // 质控评分
  '/qualityControlScore/list': { name: '质控评分管理', parent: '/index' },
  '/qualityControlScore/detail': { name: '质控评分详情', parent: '/qualityControlScore/list' },

  // 入驻申请
  '/settledApply/list': { name: '入驻申请', parent: '/index' },

  // 活动管理
  '/activity/list': { name: '活动管理', parent: '/index' },
  '/activity/add': { name: '新增活动', parent: '/activity/list' },
  '/activity/edit': { name: '活动编辑', parent: '/activity/list' },
  '/activity/user': { name: '活动用户管理', parent: '/index' },
  '/activity/user/detail': { name: '统计详情', parent: '/activity/user' },
  '/activity/subject': { name: '活动专题管理', parent: '/index' },
  '/activity/statistics': { name: '活动数据列表', parent: '/index' },
  '/activity/statistics/detail': { name: '统计详情', parent: '/activity/statistics' },
  '/activity/operational': { name: '活动运营分析', parent: '/index' },

  // 联合病房
  '/combinedWard/mdt': { name: '联合病房MDT团队', parent: '/index' },
  '/combinedWard/mdtDetail': { name: '团队详情', parent: '/combinedWard/mdt' },
  '/combinedWard/mdtConsult': { name: '团队会诊中心', parent: '/index' },
  '/combinedWard/mdtConsultCreate': { name: '创建会诊', parent: '/combinedWard/mdtConsult' },
  '/combinedWard/mdtConsultDetail': { name: '会诊详情', parent: '/combinedWard/mdtConsult' },
  '/combinedWard/scoreResults': { name: '确认评分结果', parent: '/patient/list' },
  '/combinedWard/qualityScoreDetail': { name: '质控参考评分详情', parent: '/patient/list' },
  '/combinedWard/timelineLecture': { name: '编辑病例时间轴', parent: '/patient/list' },
  '/combinedWard/caseRecordsComments': {
    name: '病例更新及点评记录',
    parent: '/combinedWard/mdtDetail',
    back: true,
  },
  '/combinedWard/picSmaller': { name: '图小小', parent: '/index' },
  '/combinedWard/imageRecognite': { name: '图像识别管理', parent: '/index' },
  '/combinedWard/recogniteDetail': { name: '图像识别详情', parent: '/combinedWard/imageRecognite' },
  '/combinedWard/registrationApplication': { name: '注册申请', parent: '/index' },
  '/combinedWard/exportBasicInfo': { name: '基本信息导出', parent: '/index' },
  '/combinedWard/pptEditor': { name: '图片识别详情', parent: '/index' },
  '/combinedWard/mpleMindMap': { name: 'MDT时间轴', parent: '/index' },
  '/combinedWard/imChat': { name: '图文会诊', parent: '/combinedWard/mdtConsult' },

  // 数据统计
  '/dataBase': { name: '数据统计', parent: '/index' },
  '/mdt-statistics': { name: 'MDT数据统计', parent: '/index' },

  // pcp
  '/registerManagement/doctorRegistrChannel': { name: '注册渠道管理', parent: '/index' },
  '/registerManagement/patientRegistrScene': { name: '患者注册场景管理', parent: '/index' },
  '/registerManagement/registrSceneForm': {
    name: '编辑场景码配置',
    parent: '/registerManagement/patientRegistrScene',
    back: true,
  },
  '/userManagement/patientUserManagement': { name: '患者用户管理', parent: '/index' },
  '/userManagement/doctorUserManagement': { name: '医生用户管理', parent: '/index' },
  '/userManagement/doctorUserDetail': {
    name: '医生详情',
    parent: '/userManagement/doctorUserManagement',
  },
  '/experManagement/projectManagement': { name: '项目管理', parent: '/index' },
  '/experManagement/fileFormManagement': { name: '建档表单管理', parent: '/index' },
  '/experManagement/fileFormEditor': { name: '建档表单编辑', parent: '/index' },
  '/userManagement/workbenchPanel': { name: '工作台面板', parent: '/index' },
  '/medicalRecordManagement/patientMedicalRecord': { name: '患者病历', parent: '/index' },
  '/medicalRecordManagement/patientMedicalDetail': {
    name: '患者病历详情',
    parent: '/medicalRecordManagement/patientMedicalRecord',
  },
  '/medicalRecordManagement/photoCollectionBox': { name: '图片收集箱', parent: '/index' },
  '/sessionManagement/workflowChat': { name: '统一聊天', parent: '/index' },
  '/sessionManagement/chatLog': { name: '聊天记录', parent: '/index' },
  '/qualityControl/qualityControlList': { name: '质控列表', parent: '/index' },
  '/qualityControl/qualityControlDetail': {
    name: '质控详情',
    parent: '/qualityControl/qualityControlList',
  },
  '/qualityControl/score': { name: '质控评分管理', parent: '/index' },
  '/qualityControl/qualityScoreForm': {
    name: '质控评分详情',
    parent: '/qualityControl/score',
  },
  '/qualityControl/medicalRecordQuality': {
    name: '病历质控管理',
    parent: '/index',
  },
  '/qualityControl/overview': {
    name: '质控总览',
    parent: '/index',
  },
  '/qualityControl/dashboard': {
    name: '质控看板',
    parent: '/index',
  },
};

export default routeMetaMap;
